import React, { useState } from 'react';
import { Check, X, Loader, AlertCircle, Save } from 'lucide-react';
import { useI18n } from '../../i18n';

const API_BASE = process.env.REACT_APP_API_URL || '';

const JiraWizard = ({ onClose, onSave }) => {
  const { t } = useI18n();
  const [config, setConfig] = useState({
    url: '',
    email: '',
    api_token: '',
    project_key: 'VULN',
    issue_type: 'Bug',
    min_severity: 'high',
    auto_assign: '',
    labels: 'security,automated,sylink',
  });
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);

    try {
      // First save config to backend
      const saveResponse = await fetch(`${API_BASE}/api/v1/system/integrations/jira`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
        body: JSON.stringify({
          config: config,
          enabled: false
        }),
      });

      if (!saveResponse.ok) {
        const error = await saveResponse.json();
        throw new Error(error.detail || 'Erreur lors de la sauvegarde');
      }

      // Then test via backend API
      const response = await fetch(`${API_BASE}/api/v1/system/integrations/jira/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
      });

      const result = await response.json();

      if (result.success) {
        setTestResult({
          success: true,
          message: result.message || 'Jira connection successful!'
        });
      } else {
        setTestResult({ success: false, message: result.error || 'Connection failed' });
      }
    } catch (error) {
      setTestResult({ success: false, message: error.message });
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_BASE}/api/v1/system/integrations/jira`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
        body: JSON.stringify({
          config: config,
          enabled: true
        }),
      });

      if (response.ok) {
        onSave('jira', config);
        onClose();
      } else {
        const error = await response.json();
        setTestResult({ success: false, message: error.detail || 'Erreur lors de la sauvegarde' });
      }
    } catch (error) {
      setTestResult({ success: false, message: error.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
      <div className="bg-brand-black-light rounded-xl border border-brand-green/30 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-3xl">🎫</span>
            <div>
              <h2 className="text-2xl font-bold text-brand-green">Configuration Jira</h2>
              <p className="text-sm text-gray-400">Ticketing automatique pour remediation</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Jira URL
            </label>
            <input
              type="url"
              value={config.url}
              onChange={(e) => setConfig({ ...config, url: e.target.value })}
              placeholder="https://your-domain.atlassian.net"
              className="input-field w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Email
            </label>
            <input
              type="email"
              value={config.email}
              onChange={(e) => setConfig({ ...config, email: e.target.value })}
              placeholder={t('auth.emailPlaceholder')}
              className="input-field w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              API Token
            </label>
            <input
              type="password"
              value={config.api_token}
              onChange={(e) => setConfig({ ...config, api_token: e.target.value })}
              placeholder="Generated in Account Settings → API Tokens"
              className="input-field w-full"
            />
            <p className="text-xs text-gray-500 mt-1">
              Create a token at <a href="https://id.atlassian.com/manage-profile/security/api-tokens" target="_blank" rel="noreferrer" className="text-brand-green hover:underline">Atlassian API Tokens</a>
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Project Key
              </label>
              <input
                type="text"
                value={config.project_key}
                onChange={(e) => setConfig({ ...config, project_key: e.target.value.toUpperCase() })}
                placeholder="VULN"
                className="input-field w-full uppercase"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Issue Type
              </label>
              <select
                value={config.issue_type}
                onChange={(e) => setConfig({ ...config, issue_type: e.target.value })}
                className="input-field w-full"
              >
                <option value="Bug">Bug</option>
                <option value="Task">Task</option>
                <option value="Security Vulnerability">Security Vulnerability</option>
                <option value="Story">Story</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Minimum severity to create a ticket
            </label>
            <select
              value={config.min_severity}
              onChange={(e) => setConfig({ ...config, min_severity: e.target.value })}
              className="input-field w-full"
            >
              <option value="critical">Critical seulement</option>
              <option value="high">High et Critical</option>
              <option value="medium">Medium, High et Critical</option>
              <option value="low">All severities</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Auto-assign to (optional)
            </label>
            <input
              type="email"
              value={config.auto_assign}
              onChange={(e) => setConfig({ ...config, auto_assign: e.target.value })}
              placeholder="assignataire@exemple.fr"
              className="input-field w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Labels (comma-separated)
            </label>
            <input
              type="text"
              value={config.labels}
              onChange={(e) => setConfig({ ...config, labels: e.target.value })}
              placeholder="security,automated,sylink"
              className="input-field w-full"
            />
          </div>

          {/* Test */}
          <div className="pt-4 border-t border-gray-800">
            <button
              onClick={handleTest}
              disabled={testing || !config.url || !config.email || !config.api_token}
              className="btn-green w-full flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {testing ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  <span>Test en cours...</span>
                </>
              ) : (
                <>
                  <Check className="w-5 h-5" />
                  <span>Tester la connexion</span>
                </>
              )}
            </button>

            {testResult && (
              <div className={`mt-4 p-4 rounded-lg border flex items-start gap-3 ${
                testResult.success
                  ? 'bg-brand-green/10 border-brand-green/50'
                  : 'bg-red-500/10 border-red-500/50'
              }`}>
                {testResult.success ? (
                  <Check className="w-5 h-5 text-brand-green flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                )}
                <div>
                  <div className={`font-medium ${testResult.success ? 'text-brand-green' : 'text-red-400'}`}>
                    {testResult.success ? 'Success' : 'Failure'}
                  </div>
                  <div className="text-sm text-gray-400 mt-1">{testResult.message}</div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="p-6 border-t border-gray-800 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700"
          >
            Annuler
          </button>
          <button
            onClick={handleSave}
            disabled={!testResult?.success || saving}
            className="btn-green disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                <span>Sauvegarde...</span>
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                <span>Sauvegarder & Activer</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default JiraWizard;
